import React, { useState, useContext, useEffect } from 'react';
import NumberFormat from 'react-number-format';
import axios from 'axios';
import { getApi } from '../../services/api';

// @material-ui/core components
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Snackbar from '@material-ui/core/Snackbar';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import { Doughnut, Pie } from 'react-chartjs-2';

// mobx context
import { observer } from 'mobx-react';
import { clientsRef } from '../../tools/firebase';
import PersonalStoreContext from '../../stores/PersonalStore';
import GoalStoreContext from '../../stores/GoalStore';
import AssetStoreContext from '../../stores/AssetStore';
import SavingCapacityStoreContext from '../../stores/SavingCapacityStore';
import GlobalStoreContext from '../../stores/globalStore';

// core assets
import SadFace from '../../assets/img/sadface.png';
import HappyFace from '../../assets/img/happyface.png';
import contactUS from '../../assets/img/popup_contact_us.png';
import { yellow } from '@material-ui/core/colors';

//** css */
import './css/results.css';

// core utils
import {
  getCivilStatusByValue,
  getProfessionByValue,
  savingObjectivesListEs,
  savingObjectivesListEn,
  assetsListEs,
  assetsListEn,
  passivesListEs,
  passivesListEn,
  colorPalette
} from '../../utils/Constants';

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1
  },
  paper: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary
  },
  spanValues: {
    color: '#000',
    fontSize: '16px',
    width: '100%',
    wordBreak: 'break-word',
    margin: '0px',
    fontWeight: '600'
  },
  gridComponent: {
    flexGrow: 1
  },
  gridPaper: {
    paddingLeft: '15px',
    paddingRight: '15px',
    textAlign: 'justify'
  },
  gridLabel: {
    color: '#000',
    fontSize: '20px',
    lineHeight: '135%',
    width: '100%',
    wordBreak: 'break-word',
    margin: '0px',
    fontWeight: '600'
  }
}));

const getCanvasGraphicImage = hrefObject => {
  if (hrefObject) {
    return hrefObject.current.chartInstance.canvas
      .toDataURL('image/png')
      .split(';base64,')
      .pop();
  }
};

const options = {
  legend: {
    display: true,
    position: 'bottom'
  },
  tooltips: {
    callbacks: {
      label: function(tooltipItems, data) {
        return `${data.labels[tooltipItems.index]}: $${data.datasets[0].data[tooltipItems.index]
          .toString()
          .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}`;
      }
    }
  }
};

const Results = observer(({ getImages, setGetImages, setOpen, lang }) => {
  const savingObjectivesList = lang.lang === 'es' ? savingObjectivesListEs : savingObjectivesListEn;
  const passivesList = lang.lang === 'es' ? passivesListEs : passivesListEn;

  const assetsList = lang.lang === 'es' ? assetsListEs : assetsListEn;
  const graphicAsset = React.createRef();
  const graphicPassive = React.createRef();
  const graphicPatrimony = React.createRef();
  const graphicSaving = React.createRef();
  const refGraphicEmergency = React.useRef();

  const classes = useStyles();
  const personalStore = useContext(PersonalStoreContext);
  const goalStore = useContext(GoalStoreContext);
  const assetStore = useContext(AssetStoreContext);
  const savingStore = useContext(SavingCapacityStoreContext);
  const globalStore = useContext(GlobalStoreContext);
  const { emailSent, assetsImg, passivesImg, patrimonyImg, savingImg } = globalStore;
  const [openSnack, setOpenSnack] = React.useState(false);
  const [messageSnack, setMessageSnack] = React.useState('');
  const [openMessage, setOpenMessage] = useState(false);

  const [isSendingContactRequest, setSendingContactRequest] = useState(false);
  const [isContacted, setIsContacted] = useState(false);
  const [dataAssets, setDataAssets] = useState({
    labels: [''],
    datasets: [
      {
        data: [],
        backgroundColor: [],
        hoverBackgroundColor: []
      }
    ]
  });
  const [dataPassives, setDataPassives] = useState({
    labels: [''],
    datasets: [
      {
        data: [],
        backgroundColor: [],
        hoverBackgroundColor: []
      }
    ]
  });
  const [dataPatrimony, setDataPatrimony] = useState(null);

  const annualIncome = (savingStore.incomes * 12).toFixed(2);
  const annualExpense = (savingStore.expenses * 12).toFixed(2);

  const data = {
    labels: [lang.incomeAnnual, lang.expenseByYear],
    datasets: [
      {
        data: [annualIncome, annualExpense],
        backgroundColor: colorPalette.savingCapacity.backgroundColor, // ['#FF6384', '#36A2EB'],
        hoverBackgroundColor: colorPalette.savingCapacity.hoverBackgroundColor // ['#FF6384', '#36A2EB'],
      }
    ]
  };

  useEffect(
    () => {
      const updateAssetsData = () => {
        if (assetStore.assetsList.length > 0) {
          setDataAssets({
            labels: assetStore.assetsList.map(x => assetsList.find(y => y.value === x.name).name),
            datasets: [
              {
                data: assetStore.assetsList.map(x => x.amount),
                backgroundColor: assetStore.assetsList.map(
                  (x, index) => colorPalette.assets.backgroundColor[index]
                ),
                hoverBackgroundColor: assetStore.assetsList.map(
                  (x, index) => colorPalette.assets.hoverBackgroundColor[index]
                )
              }
            ]
          });
        }
      };

      const updatePassivesData = () => {
        if (assetStore.passivesList.length > 0) {
          setDataPassives({
            labels: assetStore.passivesList.map(
              x => passivesList.find(y => y.value === x.name).name
            ),
            datasets: [
              {
                data: assetStore.passivesList.map(x => x.amount),
                backgroundColor: assetStore.passivesList.map(
                  (x, index) => colorPalette.passives.backgroundColor[index]
                ),
                hoverBackgroundColor: assetStore.passivesList.map(
                  (x, index) => colorPalette.passives.hoverBackgroundColor[index]
                )
              }
            ]
          });
        }
      };

      const updatePatrimony = () => {
        if (dataPatrimony === null) {
          setDataPatrimony({
            labels: [lang.resultAssets, lang.resultPassives],
            datasets: [
              {
                data: [assetStore.totalAssets, assetStore.totalPassive],
                backgroundColor: colorPalette.patrimony.backgroundColor, // ['#5c94bd', '#1a3e59'],
                hoverBackgroundColor: colorPalette.patrimony.hoverBackgroundColor // ['#5c94bd', '#1a3e59'],
              }
            ]
          });
        }
      };

      updateAssetsData();
      updatePassivesData();
      updatePatrimony();
    },
    // eslint-disable-next-line
    [
      assetStore.assetsList,
      assetStore.passivesList,
      assetStore.totalAssets,
      assetStore.totalPassive,
      dataPatrimony
    ]
  );

  useEffect(
    () => {
      if (!emailSent && assetsImg && passivesImg && patrimonyImg && savingImg) {
        onSubmitResults();
        globalStore.setValue('emailResults', true);
      }
    },
    // eslint-disable-next-line
    [emailSent, assetsImg, passivesImg, patrimonyImg, savingImg]
  );

  // *** Check if graphic image its rendered for send email with the results
  const chartPlugins = [
    {
      afterRender: function(chart) {
        if (!globalStore.emailSent) {
          switch (chart.id) {
            case 2:
              // assets id:2
              globalStore.setValue('assetsImg', true);
              break;
            case 3:
              // passives id:3
              globalStore.setValue('passivesImg', true);
              break;
            case 4:
              // savings id:4
              globalStore.setValue('savingImg', true);
              break;
            default:
              // patrimony id:5
              globalStore.setValue('patrimonyImg', true);
              break;
          }
        }
      }
    }
  ];

  const handleCloseSnack = (_, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setMessageSnack('');
    setOpenSnack(false);
  };

  const insertClient = client => {
    clientsRef.on('value', async snapchop => {
      const clientList = [];
      const clientsFirebase = await snapchop.val();

      for (const key in clientsFirebase) {
        clientList.push(clientsFirebase[key].email);
      }

      if (!clientList.includes(client.email)) {
        clientsRef.push().set(client);
      }
    });
  };

  const onSubmitResults = async _ => {
    // create image from canvas
    const assetsImg = getCanvasGraphicImage(graphicAsset);
    const passiveImg = getCanvasGraphicImage(graphicPassive);
    const patrimonyImg = getCanvasGraphicImage(graphicPatrimony);
    const savingImg = getCanvasGraphicImage(graphicSaving);

    const parseGoals =
      goalStore.goals && goalStore.goals.length > 0
        ? goalStore.goals.map(
            item =>
              `${savingObjectivesList.find(x => x.value === item.name).label}, ${item.description}`
          )
        : '';

    const body = {
      lang: lang.lang,
      total_assets: assetStore.totalAssets,
      total_passive: assetStore.totalPassive,
      total_patrimony: assetStore.patrimony,
      annual_incomes: annualIncome,
      annual_expenses: annualExpense,
      total_saving: annualIncome - annualExpense,
      name: personalStore.name,
      email: personalStore.email,
      civil_status: getCivilStatusByValue(personalStore.civilStatus),
      profession: getProfessionByValue(personalStore.profession),
      dependentAmount: personalStore.haveDependent
        ? `${lang.resultHaveDependent1} ${personalStore.dependentAmount} ${lang.resultHaveDependent2}`
        : `${lang.resultNoDependent}`,
      goals: parseGoals,
      emergency_founds: goalStore.hasSavingDeposit
        ? `${lang.resultHaveEmergencyFund}`
        : `${lang.resultNoEmergencyFund}`,
      img_assets: assetsImg,
      img_passive: passiveImg,
      img_saving: savingImg,
      img_patrimony: patrimonyImg,
      emergencyFoundsImage: goalStore.hasSavingDeposit
    };

    try {
      const response = await getApi().post('diagnostic/result', body);
      if (response.status === 200) {
        setMessageSnack(lang.resultSendReport);
        setOpenSnack(true);
        // setIsResultSending(true);
        insertClient({
          name: personalStore.name,
          email: personalStore.email,
          phone: personalStore.phone
        });
        // setOpen(true);
      }
    } catch (error) {
      setMessageSnack(lang.resultErrorInSent);
      setOpenSnack(true);
    }
  };

  const handleCloseMessage = () => {
    setOpenMessage(false);
  };

  const handleContactUS = async () => {
    setSendingContactRequest(true);

    try {
      const body = {
        name: personalStore.name,
        email: personalStore.email,
        civil_status: getCivilStatusByValue(personalStore.civilStatus),
        profession: getProfessionByValue(personalStore.profession)
      };
      const resp = await axios.post('/.netlify/functions/sendContact', body);
      if (resp.status === 200) {
        setOpenMessage(true);
        setIsContacted(true);
      }
    } catch (error) {
      setMessageSnack(lang.resultErrorContactUs);
      setOpenSnack(true);
    }
    setSendingContactRequest(false);
  };

  const ColorButton = withStyles(theme => ({
    root: {
      boxShadow: 'none',
      textTransform: 'none',
      fontSize: 16,
      fontWeight: 800,
      color: 'black',
      border: 0,
      borderRadius: 0,
      backgroundColor: '#ffe500',
      '&:hover': {
        backgroundColor: yellow[400]
      }
    }
  }))(Button);

  const styles = theme => ({
    root: {
      margin: 0,
      padding: theme.spacing(2)
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500]
    }
  });

  const DialogTitle = withStyles(styles)(props => {
    const { children, classes, onClose, ...other } = props;
    return (
      <MuiDialogTitle disableTypography className={classes.root} {...other}>
        <Typography variant="h6">{children}</Typography>
        {onClose ? (
          <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        ) : null}
      </MuiDialogTitle>
    );
  });

  return (
    <div className={classes.gridComponent}>
      <p>{lang.resultTittle}</p>
      <br />
      <br />
      <div className={classes.root}>
        <Grid container spacing={3}>
          <Grid item md={6} xs={12} style={{ padding: '20px' }}>
            <div className={classes.gridPaper}>
              <h3 style={{ textAlign: 'center' }}>{lang.resultContactInfo}</h3>
              <br />
              <Typography>
                {lang.resultContactInfoName}{' '}
                <span className={classes.spanValues}>{personalStore.name}</span>
              </Typography>
              <Typography>
                {lang.resultContactInfoEmail}{' '}
                <span className={classes.spanValues}>{personalStore.email}</span>
              </Typography>
              <Typography>
                {lang.resultContactInfoStatus}{' '}
                <span className={classes.spanValues}>
                  {getCivilStatusByValue(personalStore.civilStatus, lang.lang)}
                </span>
              </Typography>
              {/* <Typography>
                Dependientes económicos: <span className={classes.spanValues}>0</span>
              </Typography> */}
              <Typography>
                {lang.resultContactInfoProfession}{' '}
                <span className={classes.spanValues}>
                  {getProfessionByValue(personalStore.profession, lang.lang)}
                </span>
              </Typography>
              <br />
              <br />
              <Typography>{lang.resultDependents}</Typography>

              {personalStore.haveDependent ? (
                <Typography>
                  <span className={classes.spanValues}>
                    {lang.resultHaveDependent1} {personalStore.dependentAmount}{' '}
                    {lang.resultHaveDependent2}
                  </span>
                </Typography>
              ) : (
                <Typography>{lang.resultNoDependent}</Typography>
              )}

              <br />
              <br />
              {goalStore.goals && goalStore.goals.length > 0 && (
                <>
                  <br />
                  <Typography>{lang.resultObjectives}</Typography>
                  <ul>
                    {goalStore.goals.map((item, index) => (
                      <li key={index}>
                        <span className={classes.spanValues}>
                          {savingObjectivesList.find(x => x.value === item.name).label}
                          {', '}
                          {item.description}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </Grid>

          <Grid item md={6} xs={12}>
            <div className={classes.gridPaper} style={{ textAlign: 'center' }}>
              <h3>{lang.resultEmergencyFund}</h3>
              <br />
              <Grid container>
                <Grid item sm={4} xs={8}>
                  <img
                    src={goalStore.hasSavingDeposit ? HappyFace : SadFace}
                    ref={refGraphicEmergency}
                    style={{ width: '100px' }}
                    alt="Happy Face"
                  />
                </Grid>
                <Grid item sm={8} xs={8} style={{ textAlign: 'left' }}>
                  <p>
                    {goalStore.hasSavingDeposit
                      ? lang.resultHaveEmergencyFund
                      : lang.resultNoEmergencyFund}
                  </p>
                </Grid>
              </Grid>
            </div>
          </Grid>

          <Grid item md={6} xs={12}>
            <div className={classes.gridPaper} style={{ textAlign: 'center' }}>
              <h3>{lang.resultConsolidate}</h3>
              <br />
              <p>{lang.resultAssets}</p>
              <Doughnut
                ref={graphicAsset}
                data={dataAssets}
                options={options}
                plugins={chartPlugins}
              />
              <br />
              <p>{lang.resultPassives}</p>
              <Doughnut
                ref={graphicPassive}
                data={dataPassives}
                options={options}
                plugins={chartPlugins}
              />
              <br />
              {dataPatrimony && (
                <>
                  <p>{lang.resultConsolidatePatrimony}</p>
                  <Pie
                    ref={graphicPatrimony}
                    data={dataPatrimony}
                    options={options}
                    plugins={chartPlugins}
                  />
                </>
              )}
              <br />
              <br />
              <Grid container style={{ padding: '20px' }}>
                <Grid
                  item
                  md={6}
                  xs={6}
                  style={{
                    textAlign: 'initial',
                    borderBottom: '1px solid gray'
                  }}
                >
                  <p>{lang.resultAssets}:</p>
                </Grid>
                <Grid
                  item
                  md={6}
                  xs={6}
                  style={{
                    textAlign: 'end',
                    borderBottom: '1px solid gray',
                    paddingTop: '15px',
                    fontWeight: 'bold'
                  }}
                >
                  <span>
                    <NumberFormat
                      value={assetStore.totalAssets}
                      displayType="text"
                      thousandSeparator
                      prefix="$"
                    />
                    {/* ${assetStore.totalAssets} */}
                  </span>
                </Grid>

                <Grid
                  item
                  md={6}
                  xs={6}
                  style={{
                    textAlign: 'initial',
                    borderBottom: '1px solid gray'
                  }}
                >
                  <p>{lang.resultPassives}:</p>
                </Grid>
                <Grid
                  item
                  md={6}
                  xs={6}
                  style={{
                    textAlign: 'end',
                    borderBottom: '1px solid gray',
                    paddingTop: '15px',
                    fontWeight: 'bold'
                  }}
                >
                  <span>
                    <NumberFormat
                      value={assetStore.totalPassive}
                      displayType="text"
                      thousandSeparator
                      prefix="$"
                    />
                    {/* ${assetStore.totalPassive} */}
                  </span>
                </Grid>

                <Grid
                  item
                  md={6}
                  xs={6}
                  style={{
                    textAlign: 'initial',
                    borderBottom: '1px solid gray',
                    fontWeight: 'bold'
                  }}
                >
                  <p>{lang.resultConsolidatePatrimonyTittle}</p>
                </Grid>
                <Grid
                  item
                  md={6}
                  xs={6}
                  style={{
                    textAlign: 'end',
                    borderBottom: '1px solid gray',
                    paddingTop: '15px',
                    fontWeight: 'bold'
                  }}
                >
                  <span>
                    <NumberFormat
                      value={assetStore.patrimony}
                      displayType="text"
                      thousandSeparator
                      prefix="$"
                    />
                    {/* ${assetStore.patrimony} */}
                  </span>
                </Grid>
              </Grid>
            </div>
          </Grid>

          <Grid item md={6} xs={12}>
            <div className={classes.gridPaper} style={{ textAlign: 'center' }}>
              {/* <SavingCapacity
                  annualIncome={(savingStore.incomes * 12).toFixed(2)}
                  annualExpense={(savingStore.expenses * 12).toFixed(2)}
                /> */}
              <div id="chart">
                <h3>{lang.savingDescription}</h3>
                <br />
                <br />
                <Pie data={data} ref={graphicSaving} options={options} plugins={chartPlugins} />
                <br />
                <br />
                <Grid container style={{ padding: '20px' }}>
                  <Grid
                    item
                    md={6}
                    xs={6}
                    style={{
                      textAlign: 'initial',
                      borderBottom: '1px solid gray'
                    }}
                  >
                    <p>{lang.incomeAnnual}:</p>
                  </Grid>
                  <Grid
                    item
                    md={6}
                    xs={6}
                    style={{
                      textAlign: 'end',
                      borderBottom: '1px solid gray',
                      paddingTop: '15px',
                      fontWeight: 'bold'
                    }}
                  >
                    <span>
                      <NumberFormat
                        value={annualIncome}
                        displayType="text"
                        thousandSeparator
                        prefix="$"
                      />
                      {/* ${annualIncome} */}
                    </span>
                  </Grid>

                  <Grid
                    item
                    md={6}
                    xs={6}
                    style={{
                      textAlign: 'initial',
                      borderBottom: '1px solid gray'
                    }}
                  >
                    <p>{lang.expenseByYear}:</p>
                  </Grid>
                  <Grid
                    item
                    md={6}
                    xs={6}
                    style={{
                      textAlign: 'end',
                      borderBottom: '1px solid gray',
                      paddingTop: '15px',
                      fontWeight: 'bold'
                    }}
                  >
                    <span>
                      <NumberFormat
                        value={annualExpense}
                        displayType="text"
                        thousandSeparator
                        prefix="$"
                      />
                      {/* ${annualExpense} */}
                    </span>
                  </Grid>

                  <Grid
                    item
                    md={6}
                    xs={6}
                    style={{
                      textAlign: 'initial',
                      borderBottom: '1px solid gray',
                      fontWeight: 'bold'
                    }}
                  >
                    <p>{lang.savingDescription}</p>
                  </Grid>
                  <Grid
                    item
                    md={6}
                    xs={6}
                    style={{
                      textAlign: 'end',
                      borderBottom: '1px solid gray',
                      paddingTop: '15px',
                      fontWeight: 'bold'
                    }}
                  >
                    <span>
                      <NumberFormat
                        value={(annualIncome - annualExpense).toFixed(2)}
                        displayType="text"
                        thousandSeparator
                        prefix="$"
                      />
                      {/* ${annualIncome - annualExpense} */}
                    </span>
                  </Grid>
                </Grid>
              </div>
            </div>
          </Grid>

          <Grid item md={12} xs={12} className="recommendation-grid">
            <h3 className="recommendation">{lang.recommendation}</h3>
            <p>{lang.recommendationMessage}</p>
          </Grid>
        </Grid>
      </div>
      <br />
      {!isContacted && (
        <ColorButton
          variant="contained"
          color="primary"
          className={classes.margin}
          onClick={handleContactUS}
          disabled={isSendingContactRequest}
        >
          {lang.optionContact}
        </ColorButton>
      )}

      <Dialog
        open={openMessage}
        onClose={handleCloseMessage}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="customized-dialog-title" onClose={handleCloseMessage}></DialogTitle>
        <DialogContent style={{ marginTop: '14px' }}>
          <DialogContentText id="alert-dialog-description">
            {/* Let Google help apps determine location. This means sending anonymous location data to
            Google, even when no apps are running. */}

            {lang.lang === 'es' ? (
              <img src={contactUS} alt="Thanks for contact us!" />
            ) : (
              <p>One of our advisers will contact you very soon</p>
            )}
          </DialogContentText>
        </DialogContent>
      </Dialog>

      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        open={openSnack}
        autoHideDuration={6000}
        onClose={handleCloseSnack}
        message={messageSnack}
        action={
          <React.Fragment>
            <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseSnack}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </React.Fragment>
        }
      />
    </div>
  );
});

export default Results;
